import {
  ApiAction,
  ApiContext,
  ApiItemType,
  ApiReturnFrontendPermission,
} from '@highimpact-frontend/api-types';
import { useEffect, useMemo, useState } from 'react';
import { api_permissions } from '../../../../api/api-commons';

export type UseCheckPermissionResult = {
  isLoading: boolean; //true, when request is running
  error: Error | null;
  /**
   * Action define how to display element
   * "hide" | "hide_with_message" | "disable" | "display"
   */
  action: ApiAction;
  data: ApiReturnFrontendPermission | null;

  /** Comfortables booleans flags for simplify verification */
  canDisplay: boolean;
  canDisable: boolean;
  shouldHide: boolean;
};

/** 
Hook requests access rights from the API for the element.
Returns:
  Download status.
  Error (if any).
  Data and action.
  Convenient Boolean flags.

usePermission simplifies integration with TSX:
  Hides the element.
  Shows it with a message.
  Shows it, but makes it disabled. * 
 */

// Fetcher function
const fetcher = async (
  url: string,
  params: ApiContext,
  signal: AbortSignal
) => {
  const res = await api_permissions.post(url, params, { signal });
  if (!res.data) throw new Error('Failed to fetch permissions');
  return res.data;
};

export function useCheckPermission(
  item: ApiItemType,
  project_id?: number,
  site_id?: number,
  company_id?: number
): UseCheckPermissionResult {
  const params: ApiContext = useMemo(
    () => ({
      item,
      //transform all id to numbers or null for api recive correct data
      project_id: project_id ? Number(project_id) : null,
      site_id: site_id ? Number(site_id) : null,
      company_id: company_id ? Number(company_id) : null,
    }),
    [company_id, item, project_id, site_id]
  );

  /**
   * Data state handeling.
   * data - response from api with permissions
   * error - eroor of request
   * isLoading - flag of loading
   */
  const [data, setData] = useState<ApiReturnFrontendPermission | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();
    setIsLoading(true);

    fetcher('/frontend-permissions', params, controller.signal)
      .then((res) => {
        if (isMounted) {
          setData(res);
          setError(null);
        }
      })
      .catch((err) => {
        const isCanceled = (err as Error)?.name === 'CanceledError';
        if (isMounted && !isCanceled) {
          setError(err);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [params]);

  const action: ApiAction = data?.action ?? 'hide';

  return {
    isLoading,
    error,
    data,
    action,

    /** Convenient Boolean flags */
    canDisplay: action === 'display',
    canDisable: action === 'disable',
    shouldHide: action === 'hide' || action === 'hide_with_message',
  };
}

export function usePermission(
  item: ApiItemType,
  ...ids: any
): {
  shouldRender: boolean;
  action: ApiAction;
  message?: string;
  props: object;
} {
  const result = useCheckPermission(item, ...ids);

  if (result.action === 'hide') {
    return { shouldRender: false, action: 'hide', props: {} };
  }

  if (result.action === 'hide_with_message') {
    return {
      shouldRender: true,
      action: 'hide_with_message',
      message:
        "Vous n'avez pas les permissions nécessaires pour accéder à cette page",
      props: {},
    };
  }

  if (result.action === 'disable') {
    return {
      shouldRender: true,
      action: 'disable',
      props: {
        style: { pointerEvents: 'none' as const, opacity: 0.5 },
      },
    };
  }

  return { shouldRender: true, action: 'display', props: {} };
}
